import {
  type CustomerDashboardAccount,
  type CustomerMembershipActionResponse,
  type CustomerOrderCompletion,
  type CustomerPaymentMethodActionResponse,
  type CustomerPaymentMethodSetup,
  type CustomerPurchaseCatalog,
  type CustomerPurchaseCoupon,
  type CustomerPurchaseRequest,
  type CustomerPurchaseResponse,
  type CustomerPurchaseSetupIntent,
  type CustomerRewardRedemptionResponse,
  type CustomerSessionResponse,
  type CustomerSubscriptionActionResponse,
  type CustomerTenant,
  customerDashboardAccountSchema,
  customerMembershipActionResponseSchema,
  customerOrderCompletionSchema,
  customerPaymentMethodActionResponseSchema,
  customerPaymentMethodSetupSchema,
  customerPurchaseCatalogSchema,
  customerPurchaseCouponSchema,
  customerPurchaseResponseSchema,
  customerPurchaseSetupIntentSchema,
  customerRewardRedemptionResponseSchema,
  customerSessionResponseSchema,
  customerSubscriptionActionResponseSchema,
  customerTenantSchema,
  type JourneyRequest,
  type MemberHome,
  memberHomeSchema,
  type SignupDetailsRequest,
  type SignupDetailsResponse,
  type SignupDiscordLinkResponse,
  type SignupErrorCode,
  type SignupJoinResponse,
  type SignupPage,
  type SignupStartResponse,
  type SignupState,
  type SignupVerifyEmailResponse,
  signupDetailsResponseSchema,
  signupDiscordLinkResponseSchema,
  signupErrorSchema,
  signupJoinResponseSchema,
  signupPageSchema,
  signupStartResponseSchema,
  signupStateSchema,
  signupVerifyEmailResponseSchema,
  type TouchConsentRequest,
  type TouchRequest,
  type TouchResponse,
  touchResponseSchema,
} from "@ternary/contracts"

export class CustomerApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly authorizationUrl: string | null,
    readonly requestId: string | null,
  ) {
    super(message)
    this.name = "CustomerApiError"
  }
}

export interface CustomerRequestContext {
  signal?: AbortSignal
}

function responseRequestId(response: Response) {
  return response.headers?.get("x-request-id") ?? null
}

async function getJson(path: string, context: CustomerRequestContext = {}): Promise<unknown> {
  const response = await fetch(path, {
    headers: { Accept: "application/json" },
    signal: context.signal ?? null,
  })

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      authorizationUrl?: unknown
      error?: unknown
    } | null
    throw new CustomerApiError(
      typeof body?.error === "string"
        ? body.error
        : `Request failed with status ${response.status}`,
      response.status,
      typeof body?.authorizationUrl === "string" ? body.authorizationUrl : null,
      responseRequestId(response),
    )
  }

  return response.json()
}

async function postJson(
  path: string,
  body: unknown,
  headers: Record<string, string> = {},
  context: CustomerRequestContext = {},
): Promise<unknown> {
  const response = await fetch(path, {
    body: JSON.stringify(body),
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...headers,
    },
    method: "POST",
    signal: context.signal ?? null,
  })
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as {
      authorizationUrl?: unknown
      error?: unknown
    } | null
    throw new CustomerApiError(
      typeof payload?.error === "string"
        ? payload.error
        : `Request failed with status ${response.status}`,
      response.status,
      typeof payload?.authorizationUrl === "string" ? payload.authorizationUrl : null,
      responseRequestId(response),
    )
  }
  return response.json()
}

async function responseError(response: Response, fallback: string) {
  try {
    const body = (await response.json()) as { error?: unknown }
    return typeof body.error === "string" ? body.error : fallback
  } catch {
    return fallback
  }
}

/**
 * A tracking write (`/touch` answers 200, the others 204). It is kept alive, so it still completes when the
 * page unloads right after, such as before the Discord redirect.
 */
async function sendTrackingWrite(path: string, method: "PATCH" | "POST", body: unknown) {
  const response = await fetch(path, {
    body: JSON.stringify(body),
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    keepalive: true,
    method,
  })
  if (!response.ok) {
    throw new CustomerApiError(
      await responseError(response, `Request failed with status ${response.status}`),
      response.status,
      null,
      responseRequestId(response),
    )
  }
  return response
}

/**
 * `POST /api/v1/touch`, kept alive like the other tracking writes, so a click on Accept just
 * before leaving still records it. The `tny_touch` cookie it sets is HttpOnly, so keep the
 * `touchId`.
 */
export async function postTouch(touch: TouchRequest): Promise<TouchResponse> {
  const response = await sendTrackingWrite("/api/v1/touch", "POST", touch)
  return touchResponseSchema.parse(await response.json())
}

/** `PATCH /api/v1/touch/:touchId/consent`. `ads: false` strips the browser's touch identifiers. */
export async function patchTouchConsent(
  touchId: string,
  consent: TouchConsentRequest,
): Promise<void> {
  await sendTrackingWrite(`/api/v1/touch/${encodeURIComponent(touchId)}/consent`, "PATCH", consent)
}

/** `POST /api/v1/journey`. The server stores it only for this browser's own touch. */
export async function postJourney(journey: JourneyRequest): Promise<void> {
  await sendTrackingWrite("/api/v1/journey", "POST", journey)
}

/** A signup route's refusal (§3.2): its code, and the attempts a wrong email code leaves. */
export class SignupApiError extends CustomerApiError {
  constructor(
    message: string,
    status: number,
    readonly code: SignupErrorCode | null,
    readonly attemptsRemaining: number | null,
    authorizationUrl: string | null,
    requestId: string | null,
  ) {
    super(message, status, authorizationUrl, requestId)
    this.name = "SignupApiError"
  }
}

/**
 * One signup route, as JSON both ways. The browser sends the HttpOnly `tny_signup` cookie with it
 * (same origin). A refusal throws a SignupApiError parsed with `signupErrorSchema`.
 */
async function signupRequest(
  path: string,
  {
    body,
    method = "GET",
    signal,
  }: { body?: unknown; method?: "GET" | "POST"; signal?: AbortSignal },
): Promise<unknown> {
  const response = await fetch(path, {
    body: body === undefined ? null : JSON.stringify(body),
    headers:
      body === undefined
        ? { Accept: "application/json" }
        : { Accept: "application/json", "Content-Type": "application/json" },
    method,
    signal: signal ?? null,
  })
  if (!response.ok) {
    const parsed = signupErrorSchema.safeParse(await response.json().catch(() => null))
    const refusal = parsed.success ? parsed.data : null
    throw new SignupApiError(
      refusal?.message ?? `Request failed with status ${response.status}`,
      response.status,
      refusal?.error ?? null,
      refusal?.attemptsRemaining ?? null,
      refusal?.authorizationUrl ?? null,
      responseRequestId(response),
    )
  }
  return response.json()
}

const signupPath = (id: string, action?: string) =>
  `/api/v1/signup/${encodeURIComponent(id)}${action ? `/${action}` : ""}`

/** `GET /api/v1/signup/:slug`: the public signup page (cached 60 s by the server). */
export async function loadSignupPage(
  slug: string,
  context: CustomerRequestContext = {},
): Promise<SignupPage> {
  return signupPageSchema.parse(await signupRequest(signupPath(slug), context))
}

/**
 * `POST /api/v1/signup/:slug/start`, as JSON (anything else is a 415, R65). Call it only when the
 * browser holds no signup for the slug: a second start replaces the `tny_signup` cookie.
 */
export async function startSignup(slug: string, touchId?: string): Promise<SignupStartResponse> {
  return signupStartResponseSchema.parse(
    await signupRequest(signupPath(slug, "start"), {
      body: touchId ? { touchId } : {},
      method: "POST",
    }),
  )
}

/** `POST /api/v1/signup/:signupId/details`, which also sends (or resends) the email code. */
export async function submitSignupDetails(
  signupId: string,
  details: SignupDetailsRequest,
): Promise<SignupDetailsResponse> {
  return signupDetailsResponseSchema.parse(
    await signupRequest(signupPath(signupId, "details"), { body: details, method: "POST" }),
  )
}

/** `POST /api/v1/signup/:signupId/verify-email`. A repeat after success answers 200 again. */
export async function verifySignupEmail(
  signupId: string,
  code: string,
): Promise<SignupVerifyEmailResponse> {
  return signupVerifyEmailResponseSchema.parse(
    await signupRequest(signupPath(signupId, "verify-email"), { body: { code }, method: "POST" }),
  )
}

/** `GET /api/v1/signup/:signupId/state` (R14), to resume a held signup. */
export async function loadSignupState(
  signupId: string,
  context: CustomerRequestContext = {},
): Promise<SignupState> {
  return signupStateSchema.parse(await signupRequest(signupPath(signupId, "state"), context))
}

/** Where "Continue with Discord" goes: a 302 to Discord's authorize page. */
export function signupDiscordPath(signupId: string): string {
  return signupPath(signupId, "discord")
}

/**
 * `POST /api/v1/signup/:signupId/discord/confirm` (R73): the code emailed to the signup, which
 * links the Discord account held for this browser.
 */
export async function confirmSignupDiscord(
  signupId: string,
  code: string,
): Promise<SignupDiscordLinkResponse> {
  return signupDiscordLinkResponseSchema.parse(
    await signupRequest(signupPath(signupId, "discord/confirm"), {
      body: { code },
      method: "POST",
    }),
  )
}

/**
 * `POST /api/v1/signup/:signupId/discord/continue` (R73): the explicit switch to the Discord
 * account held for this browser, replacing the account or signup it holds.
 */
export async function continueSignupDiscord(signupId: string): Promise<SignupDiscordLinkResponse> {
  return signupDiscordLinkResponseSchema.parse(
    await signupRequest(signupPath(signupId, "discord/continue"), { body: {}, method: "POST" }),
  )
}

/** `GET /api/v1/customer/session`: who this browser is signed in as, if anyone. */
export async function loadCustomerSession(
  context: CustomerRequestContext = {},
): Promise<CustomerSessionResponse> {
  return customerSessionResponseSchema.parse(await getJson("/api/v1/customer/session", context))
}

/**
 * `POST /api/v1/signup/:signupId/join`: adds the member to the server with the product's roles. A
 * member already in it answers 200 again. Sent as JSON `{}`, like `/start`, so another site
 * cannot send it without a CORS preflight.
 */
export async function joinSignup(signupId: string): Promise<SignupJoinResponse> {
  return signupJoinResponseSchema.parse(
    await signupRequest(signupPath(signupId, "join"), { body: {}, method: "POST" }),
  )
}

/**
 * `GET /api/v1/member/home`: the products the signed-in Discord account joined. Without a session
 * it is a 401 `not_signed_in` with the `authorizationUrl` to log in.
 */
export async function loadMemberHome(context: CustomerRequestContext = {}): Promise<MemberHome> {
  return memberHomeSchema.parse(await signupRequest("/api/v1/member/home", context))
}

export async function loadCustomerPurchase(
  slug: string | null,
  context: CustomerRequestContext = {},
): Promise<CustomerPurchaseCatalog> {
  const query = slug ? `?password=${encodeURIComponent(slug)}` : ""
  return customerPurchaseCatalogSchema.parse(await getJson(`/api/v1/purchase${query}`, context))
}

export async function loadCustomerPurchaseCoupon(
  slug: string | null,
  productId: string,
  code: string,
): Promise<CustomerPurchaseCoupon> {
  const query = new URLSearchParams({ product: productId })
  const checkoutPath = slug ? `/api/v1/purchase/${encodeURIComponent(slug)}` : "/api/v1/purchase"
  const response = await fetch(`${checkoutPath}/coupons/${encodeURIComponent(code)}?${query}`, {
    headers: { Accept: "application/json" },
  })
  if (!response.ok) {
    throw new Error(await responseError(response, "Coupon not found."))
  }
  return customerPurchaseCouponSchema.parse(await response.json())
}

export async function createCustomerPurchaseSetupIntent(
  slug: string | null,
  context: CustomerRequestContext = {},
): Promise<CustomerPurchaseSetupIntent> {
  const checkoutPath = slug ? `/api/v1/purchase/${encodeURIComponent(slug)}` : "/api/v1/purchase"
  const response = await fetch(`${checkoutPath}/setup-intent`, {
    headers: { Accept: "application/json" },
    method: "POST",
    signal: context.signal ?? null,
  })
  if (!response.ok) {
    throw new Error(await responseError(response, "Could not prepare secure card entry."))
  }
  return customerPurchaseSetupIntentSchema.parse(await response.json())
}

export async function completeCustomerPurchase(
  slug: string | null,
  idempotencyKey: string,
  purchase: CustomerPurchaseRequest,
): Promise<CustomerPurchaseResponse> {
  const checkoutPath = slug ? `/api/v1/purchase/${encodeURIComponent(slug)}` : "/api/v1/purchase"
  const response = await fetch(checkoutPath, {
    body: JSON.stringify(purchase),
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "Idempotency-Key": idempotencyKey,
    },
    method: "POST",
  })
  if (!response.ok) {
    throw new Error(await responseError(response, "Purchase failed."))
  }
  return customerPurchaseResponseSchema.parse(await response.json())
}

export async function loadCustomerTenant(
  context: CustomerRequestContext = {},
): Promise<CustomerTenant> {
  return customerTenantSchema.parse(await getJson("/api/v1/tenant", context))
}

export async function loadCustomerDashboardAccount(
  returnTo?: string,
  context: CustomerRequestContext = {},
): Promise<CustomerDashboardAccount> {
  const query = returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : ""
  return customerDashboardAccountSchema.parse(
    await getJson(`/api/v1/customer/dashboard${query}`, context),
  )
}

export async function loadCustomerOrderCompletion(
  context: CustomerRequestContext = {},
): Promise<CustomerOrderCompletion> {
  return customerOrderCompletionSchema.parse(await getJson("/api/v1/order", context))
}

export async function bindCustomerMembership(
  key: string,
): Promise<CustomerMembershipActionResponse> {
  return customerMembershipActionResponseSchema.parse(
    await postJson("/api/v1/customer/membership/bind", { key }),
  )
}

export async function joinCustomerDiscord(): Promise<CustomerMembershipActionResponse> {
  return customerMembershipActionResponseSchema.parse(
    await postJson("/api/v1/customer/membership/join", {}),
  )
}

export async function unbindCustomerMembership(): Promise<CustomerMembershipActionResponse> {
  return customerMembershipActionResponseSchema.parse(
    await postJson("/api/v1/customer/membership/unbind", {}),
  )
}

export async function redeemCustomerReward(input: {
  idempotencyKey: string
  rewardId: string
}): Promise<CustomerRewardRedemptionResponse> {
  return customerRewardRedemptionResponseSchema.parse(
    await postJson(
      "/api/v1/customer/rewards/redeem",
      { rewardId: input.rewardId },
      { "Idempotency-Key": input.idempotencyKey },
    ),
  )
}

export async function createCustomerPaymentMethodSetup(
  context: CustomerRequestContext = {},
): Promise<CustomerPaymentMethodSetup> {
  return customerPaymentMethodSetupSchema.parse(
    await postJson("/api/v1/customer/billing/payment-methods/setup-intent", {}, {}, context),
  )
}

export async function addCustomerPaymentMethod(
  paymentMethodId: string,
): Promise<CustomerPaymentMethodActionResponse> {
  return customerPaymentMethodActionResponseSchema.parse(
    await postJson("/api/v1/customer/billing/payment-methods", {
      paymentMethodId,
    }),
  )
}

export async function setDefaultCustomerPaymentMethod(
  paymentMethodId: string,
): Promise<CustomerPaymentMethodActionResponse> {
  return customerPaymentMethodActionResponseSchema.parse(
    await postJson("/api/v1/customer/billing/payment-methods/default", {
      paymentMethodId,
    }),
  )
}

export async function deleteCustomerPaymentMethod(
  paymentMethodId: string,
): Promise<CustomerPaymentMethodActionResponse> {
  const response = await fetch(
    `/api/v1/customer/billing/payment-methods/${encodeURIComponent(paymentMethodId)}`,
    {
      headers: { Accept: "application/json" },
      method: "DELETE",
    },
  )
  if (!response.ok) {
    throw new CustomerApiError(
      await responseError(response, "Could not delete this payment method."),
      response.status,
      null,
      responseRequestId(response),
    )
  }
  return customerPaymentMethodActionResponseSchema.parse(await response.json())
}

export async function updateCustomerSubscriptionCancellation(input: {
  action: "cancel" | "reactivate"
  subscriptionId: string
}): Promise<CustomerSubscriptionActionResponse> {
  return customerSubscriptionActionResponseSchema.parse(
    await postJson(
      `/api/v1/customer/billing/subscriptions/${encodeURIComponent(
        input.subscriptionId,
      )}/cancellation`,
      {
        action: input.action,
      },
    ),
  )
}

export async function updateCustomerSubscriptionPlan(input: {
  coupon: string | null
  idempotencyKey: string
  planId: string
  subscriptionId: string
}): Promise<CustomerSubscriptionActionResponse> {
  return customerSubscriptionActionResponseSchema.parse(
    await postJson(
      `/api/v1/customer/billing/subscriptions/${encodeURIComponent(input.subscriptionId)}/plan`,
      {
        coupon: input.coupon,
        planId: input.planId,
      },
      { "Idempotency-Key": input.idempotencyKey },
    ),
  )
}

export async function addCustomerSubscription(input: {
  coupon: string | null
  idempotencyKey: string
  planId: string
}): Promise<CustomerSubscriptionActionResponse> {
  return customerSubscriptionActionResponseSchema.parse(
    await postJson(
      "/api/v1/customer/billing/subscriptions/add-ons",
      {
        coupon: input.coupon,
        planId: input.planId,
      },
      { "Idempotency-Key": input.idempotencyKey },
    ),
  )
}
